import { posthog, isPostHogEnabled } from "./posthog";

/**
 * Analytics service for tracking events beyond experiments.
 * Centralizes all event tracking for consistency.
 */
export const analytics = {
  /**
   * Track when a CTA is viewed.
   * @param location
   * @param ctaType
   */
  trackCtaView: (location: string, ctaType: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("cta_viewed", { location, cta_type: ctaType });
  },

  /**
   * Track when a CTA is clicked.
   * @param location
   * @param ctaType
   * @param destination
   */
  trackCtaClick: (location: string, ctaType: string, destination: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("cta_clicked", { location, cta_type: ctaType, destination });
  },

  /**
   * Track when a user starts the upgrade funnel.
   * @param source
   */
  trackUpgradeFunnelStart: (source: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("upgrade_funnel_started", { source });
  },

  /**
   * Track when a user selects a plan.
   * @param planId
   * @param source
   */
  trackPlanSelected: (planId: string, source: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("plan_selected", { plan_id: planId, source });
  },

  /**
   * Track when a user completes an upgrade.
   * @param planId
   * @param amount
   */
  trackUpgradeCompleted: (planId: string, amount: number) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("upgrade_completed", {
      plan_id: planId,
      amount,
      $set: { subscription_tier: planId },
    });
  },

  /**
   * Track revenue events for PostHog revenue tracking.
   * @param amount
   * @param planId
   * @param isRecurring
   */
  trackRevenue: (amount: number, planId: string, isRecurring: boolean) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("$revenue", {
      amount,
      plan_id: planId,
      is_recurring: isRecurring,
    });
  },

  /**
   * Track feature usage.
   * @param feature
   * @param metadata
   */
  trackFeatureUsed: (feature: string, metadata?: Record<string, unknown>) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("feature_used", { feature, ...metadata });
  },

  /**
   * Track when an upgrade CTA is dismissed.
   * @param location
   * @param ctaType
   */
  trackCtaDismissed: (location: string, ctaType: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("cta_dismissed", { location, cta_type: ctaType });
  },

  /**
   * Onboarding funnel (ONB-U5): the dashboard Setup Guide became visible to an
   * admin. First in-app step of the `business_approved → setup_guide_viewed →
   * first_service_edit → booking_link_copied → first_reservation_received`
   * funnel (see BATCH_COMPANY_ONBOARDING_PLAN §1).
   */
  trackSetupGuideViewed: () => {
    if (!isPostHogEnabled()) return;
    posthog.capture("setup_guide_viewed", {});
  },

  /**
   * Onboarding funnel (ONB-U5): an admin clicked through a Setup Guide step's
   * deep link (e.g. to Services, Branding, Team).
   * @param step - The step id (e.g. "services", "branding", "team").
   */
  trackSetupStepClicked: (step: string) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("setup_step_clicked", { step });
  },

  /**
   * Onboarding funnel (ONB-U5): all applicable Setup Guide steps reached the
   * complete state (the guide showed its "Setup complete" finish state).
   */
  trackSetupGuideCompleted: () => {
    if (!isPostHogEnabled()) return;
    posthog.capture("setup_guide_completed", {});
  },

  /**
   * Onboarding funnel (ONB-U5): the admin copied their public booking link from
   * the Setup Guide's share block.
   */
  trackBookingLinkCopied: () => {
    if (!isPostHogEnabled()) return;
    posthog.capture("booking_link_copied", {});
  },

  /**
   * Track when an operator creates a service level (ONB-U3). `fromTemplate`
   * distinguishes the template-picker path from "start from scratch".
   * @param fromTemplate - Whether the level was seeded from a curated template.
   */
  trackServiceLevelCreated: (fromTemplate: boolean) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("service_level_created", { from_template: fromTemplate });
  },

  /**
   * Track when an operator updates their enabled ride types (ONB-U3).
   * @param enabledCount - How many ride types are enabled after the change.
   */
  trackRideTypesUpdated: (enabledCount: number) => {
    if (!isPostHogEnabled()) return;
    posthog.capture("ride_types_updated", { enabled_count: enabledCount });
  },
};
